/**
 * Disable confirmation modal. Requires a ceremonial checkbox before the
 * irreversible disable action can be confirmed.
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Checkbox, Message, Icon } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { OrgDetails } from '../types';
import { i18nKeys } from '../constants';
import styles from '../DisableOrganization.module.css';

interface DisableConfirmationModalProps {
  isOpen: boolean;
  org: OrgDetails | null;
  isDisabling: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const DisableConfirmationModal: React.FC<DisableConfirmationModalProps> = ({
  isOpen,
  org,
  isDisabling,
  error,
  onConfirm,
  onCancel
}) => {
  const [isAccepted, setIsAccepted] = useState(false);

  // Reset the ceremonial checkbox whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setIsAccepted(false);
    }
  }, [isOpen, org?.id]);

  const handleClose = useCallback(() => {
    setIsAccepted(false);
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    if (isAccepted) {
      onConfirm();
    }
  }, [isAccepted, onConfirm]);

  if (!org) {
    return null;
  }

  return (
    <Modal open={isOpen} onClose={handleClose} size="small">
      <Modal.Header>
        <Icon name="warning sign" color="red" />
        <Translate value={i18nKeys.MODAL.TITLE} />
      </Modal.Header>

      <Modal.Content>
        <div className={styles.modalDetails}>
          <div className={styles.detailRow}>
            <strong>
              <Translate value={i18nKeys.DETAILS.NAME} />
            </strong>
            <span>{org.name}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>
              <Translate value={i18nKeys.DETAILS.ID} />
            </strong>
            <span>{org.id}</span>
          </div>
        </div>

        <Message negative>
          <Message.Header>
            <Icon name="exclamation triangle" />
            <Translate value={i18nKeys.MODAL.TITLE} />
          </Message.Header>
          <p>
            <Translate value={i18nKeys.MODAL.WARNING} />
          </p>
        </Message>

        <div className={styles.modalCheckbox}>
          <Checkbox
            label={I18n.t(i18nKeys.MODAL.CHECKBOX_LABEL)}
            checked={isAccepted}
            onChange={(_, data) => setIsAccepted(!!data.checked)}
            disabled={isDisabling}
          />
        </div>

        {error && (
          <Message negative>
            <p>{error}</p>
          </Message>
        )}
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleClose} disabled={isDisabling}>
          <Translate value={i18nKeys.MODAL.CANCEL_BUTTON} />
        </Button>
        <Button
          negative
          onClick={handleConfirm}
          disabled={!isAccepted || isDisabling}
          loading={isDisabling}
        >
          <Icon name="ban" />
          <Translate value={i18nKeys.MODAL.CONFIRM_BUTTON} />
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default DisableConfirmationModal;
